import { usePrice } from './use-price';
import Text from 'components/atomic/atoms/text';
import { typography } from 'design-system/tokens';
import { validateVariant } from 'ui/utils';
import type { SupportedCurrency } from 'utils/i18n-format-currency';

export const PRICE_VARIANTS = {
  HERO: 'hero',
  CARD: 'card',
  INLINE: 'inline',
} as const;

export type PriceVariant = typeof PRICE_VARIANTS[keyof typeof PRICE_VARIANTS];

const VARIANT_CONFIG = {
  [PRICE_VARIANTS.HERO]: {
    textVariant: 'h3' as const,
    component: 'h3' as const,
    fontSize: typography.responsive.displayLarge,
    fontWeight: typography.fontWeight.semibold,
    blurAmount: '6px',
  },
  [PRICE_VARIANTS.CARD]: {
    textVariant: 'h3' as const,
    component: 'h3' as const,
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    blurAmount: '4px',
  },
  [PRICE_VARIANTS.INLINE]: {
    textVariant: 'body2' as const,
    component: 'span' as const,
    fontSize: typography.fontSize.sm,
    fontWeight: undefined,
    blurAmount: '4px',
  },
};

export interface PriceProps {
  amount: number | null;
  currency: SupportedCurrency;
  locale: string;
  variant: PriceVariant;
  blur?: boolean;
}

/**
 * Price UI component
 * 
 * Displays formatted price. Tooltip/info icon should be composed
 * at the section level if needed.
 * 
 * Variant is required - no default assumed.
 */
export function Price({
  amount,
  currency,
  locale,
  variant,
  blur,
}: PriceProps) {
  const { formatted, isBlurred } = usePrice({
    amount,
    currency,
    locale,
    blur,
  });

  if (!validateVariant('Price', variant, PRICE_VARIANTS)) {
    return null;
  }

  const config = VARIANT_CONFIG[variant];

  return (
    <span data-testid="ui-price">
      <Text
        variant={config.textVariant}
        component={config.component}
        sx={{
          fontWeight: config.fontWeight,
          fontSize: config.fontSize,
          filter: isBlurred ? `blur(${config.blurAmount})` : undefined,
          userSelect: isBlurred ? 'none' : undefined,
        }}
      >
        {formatted}
      </Text>
    </span>
  );
}
